import React, {useEffect} from "react";
import {useTranslation} from "@unibuddy/intl";

import DomesticRegionAutocomplete from "shared/Register/components/LocationAutocomplete/DomesticRegionAutocomplete";
import useAuth from "shared/Auth/hooks/useAuth";

import {useUniversity} from "../../../UniversityProvider/UniversityProvider";
import {RegisterTwoFormValues} from "../RegisterPage2Form";
import {IntlNamespaces} from "../../../../../translations/types";
import {INTL_REGION, INTL_SELECT_REGION} from "../../../../../translations/signup";

type DomesticRegionFormFieldProps = {
    values: RegisterTwoFormValues;
    setFieldValue: (field: string, value: any) => void;
};

export function DomesticRegionFormField({values, setFieldValue}: DomesticRegionFormFieldProps) {
    const {t} = useTranslation(IntlNamespaces.SIGNUP);
    const {authState} = useAuth();
    const {features, country} = useUniversity();
    const userHasDomesticRegion = authState?.me?.anyUser?.domesticRegion ?? false;
    const userHasCountry = authState?.me?.anyUser?.country ?? false;
    const collectDomesticRegionOnSignup = features.get("collectDomesticRegionOnSignup") ?? false;
    let shouldShowDomesticRegion = collectDomesticRegionOnSignup;
    const countryId = values?.countrySelection?.value || userHasCountry?.id;
    const selectedDomesticRegionCountryId = values?.domesticRegionSelection?.country?.id;

    useEffect(() => {
        if (selectedDomesticRegionCountryId && selectedDomesticRegionCountryId !== countryId) {
            setFieldValue("domesticRegionSelection", undefined);
        }
    }, [countryId, selectedDomesticRegionCountryId, setFieldValue]);

    // Conditions to check:
    // If the uni doesn't have the feature enabled, we don't show the input.
    // If the uni has it enabled:
    //      - If the user has a country saved on their user AND and that country is
    //          the same as the uni country, we show this input.
    //      - If the user country and uni country are not the same, we don't show the input.
    //      - If the user doesn't have a country saved on their user yet, then we wait for
    //          them to choose a country and then try to load the domestic region component.
    //          Inside the domestic region component, we run a query to get the regions available
    //              for the country country:
    //              - If there are none, we don't show the input.
    //              - If there are regions, we show the input

    if (userHasCountry) {
        shouldShowDomesticRegion = shouldShowDomesticRegion && userHasCountry.id === country.id;
    } else {
        shouldShowDomesticRegion =
            shouldShowDomesticRegion && values?.countrySelection?.value === country.id;
    }

    if (!shouldShowDomesticRegion || (!values?.countrySelection?.value && !userHasCountry?.id))
        return null;

    return (
        <DomesticRegionAutocomplete
            countryId={values.countrySelection ? values.countrySelection.value : userHasCountry.id}
            placeholder={t(INTL_SELECT_REGION.key, INTL_SELECT_REGION.defaultValue)}
            name="domesticRegionSelection"
            label={t(INTL_REGION.key, INTL_REGION.defaultValue)}
            initialValue={userHasDomesticRegion || null}
            required
        />
    );
}
